/* ts 友好提示 */

const { toString } = Object.prototype

/**
 * @description: 判断是否是数字字符串
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isNumberStr(val: string) {
  let reg = /^[0-9]+.?[0-9]*$/
  if (reg.test(val)) {
    return true
  }
  return false
}

/**
 * @description: 判断类型
 * @param {unknown} val 变量
 * @param {string} type 类型
 * @return {*} *
*/
export function is(val: unknown, type: string) {
  return toString.call(val) === `[object ${type}]`
}

/**
 * @description: 判断是否为undefined
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isDef<T = unknown>(val?: T): val is T {
  return typeof val !== 'undefined'
}

/**
 * @description: 判断是否不为undefined
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isUnDef<T = unknown>(val?: T): val is T {
  return !isDef(val)
}

/**
 * @description: 判断是否是个对象
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isObject(val: any): val is Record<any, any> {
  return val !== null && is(val, 'Object')
}

/**
 * @description: 判断是否为空
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isEmpty<T = unknown>(val: T): val is T {
  if (isArray(val) || isString(val)) {
    return val.length === 0
  }

  if (val instanceof Map || val instanceof Set) {
    return val.size === 0
  }

  if (isObject(val)) {
    return Object.keys(val).length === 0
  }

  return false
}

/**
 * @description: 判断是否为日期对象
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isDate(val: unknown): val is Date {
  return is(val, 'Date')
}

/**
 * @description: 判断是否为null
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isNull(val: unknown): val is null {
  return val === null
}

/**
 * @description: 判断是否为等于null且等于undefined
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isNullAndUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) && isNull(val)
}

/**
 * @description: 判断是否为undefined或者null
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isNullOrUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) || isNull(val)
}

/**
 * @description: 判断是否为数字
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isNumber(val: unknown): val is number {
  return is(val, 'Number')
}

/**
 * @description: 判断是否为promise
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isPromise<T = any>(val: unknown): val is Promise<T> {
  return is(val, 'Promise') && isObject(val) && isFunction(val.then) && isFunction(val.catch)
}

/**
 * @description: 判断是否为字符串
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isString(val: unknown): val is string {
  return is(val, 'String')
}

/**
 * @description: 判断是否为函数
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isFunction(val: unknown): val is Fn {
  return typeof val === 'function'
}

/**
 * @description: 判断是否为布尔值
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isBoolean(val: unknown): val is boolean {
  return is(val, 'Boolean')
}

/**
 * @description: 判断是否为正则
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isRegExp(val: unknown): val is RegExp {
  return is(val, 'RegExp')
}

/**
 * @description: 判断是否为数组
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isArray(val: any): val is Array<any> {
  return val && Array.isArray(val)
}

/**
 * @description: 判断是否为window对象
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isWindow(val: any): val is Window {
  return typeof window !== 'undefined' && is(val, 'Window')
}

/**
 * @description: 判断是否为元素
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isElement(val: unknown): val is Element {
  return isObject(val) && !!val.tagName
}

/**
 * @description: 判断是否为Map对象
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isMap(val: unknown): val is Map<any, any> {
  return is(val, 'Map')
}

export const isServer = typeof window === 'undefined'

export const isClient = !isServer

/**
 * @description: 判断是否为web路径
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isUrl(val: string): boolean {
  const reg
    = /(((^https?:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)$/
  return reg.test(val)
}

/**
 * @description: 判断是否为图片
 * @param {unknown} val 变量
 * @return {*} *
*/
export function isImg(val: string): boolean {
  if (isString(val)) {
    return /\.(jpg|jpeg|png|gif)$/i.test(val)
  }
  return false
}
